import { FONDS_DE_PAPIER, type FondDePapier } from '@nextilt/commun';
import type { Rangement } from '../partage/apparence';
import type { ChangementDePapier, EnvoiDUnPapier, Papier } from '../partage/api/types';

/**
 * Les règles pures des papiers posés sur la carte (chantier post-it-et-listes, ticket 02), sans
 * React ni réseau, testées dans `tests/client/papiers/papiers.test.ts`. Un papier n'est pas une
 * bulle : rien ici ne touche au moteur. La place d'un papier neuf, la taille d'un neuf et les
 * bornes sont au serveur (`server/src/domain/papiers/`) : l'écran ne les recalcule pas.
 */

/**
 * Les papiers ne se montrent que sur la carte repliée de la bulle au centre : profondeur 1, rien
 * d'ouvert sur place (décision d'Igor du 2026-10-19). La place libre d'un papier neuf est
 * calculée sur cette carte-là ; dès qu'une étape s'ouvre, les bulles bougent et viendraient sous
 * les papiers.
 */
export function papiersSeMontrent(profondeur: number, ouvertesSurPlace: number): boolean {
  return profondeur === 1 && ouvertesSurPlace === 0;
}

/** Le décor d'un papier : scotch ou punaise, et un peu de travers, comme sur la maquette. */
export interface DecorDuPapier {
  attache: 'scotch' | 'punaise';
  /** En degrés, entre -2,5 et 2,5. */
  penche: number;
}

/** Une empreinte stable d'un texte (FNV-1a sur 32 bits) : le même identifiant, le même nombre. */
function empreinte(texte: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < texte.length; i += 1) {
    h ^= texte.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * Tiré une fois pour toutes d'après l'identifiant du papier : il ne change pas d'un affichage à
 * l'autre, ni d'un appareil à l'autre, et rien n'est gardé.
 */
export function decorDuPapier(id: string): DecorDuPapier {
  const h = empreinte(id);
  const attache = h % 2 === 0 ? 'scotch' : 'punaise';
  // Onze crans de -2,5 à 2,5 degrés, d'un demi-degré.
  const penche = (Math.floor(h / 2) % 11) * 0.5 - 2.5;
  return { attache, penche };
}

/** La clé du dernier papier choisi, gardé sur l'appareil. */
export const CLE_DU_DERNIER_FOND = 'nextilt.dernier-papier';

/** Le dernier papier choisi, repris au post-it suivant ; le jaune sans choix gardé. Ne lève jamais. */
export function lireLeDernierFond(rangement: () => Rangement | null | undefined): FondDePapier {
  try {
    const lu = rangement()?.getItem(CLE_DU_DERNIER_FOND);
    return FONDS_DE_PAPIER.includes(lu as FondDePapier) ? (lu as FondDePapier) : 'jaune';
  } catch {
    return 'jaune';
  }
}

/** Garde le papier choisi. Un rangement qui refuse ne fait rien d'autre que ne pas garder. */
export function garderLeDernierFond(rangement: () => Rangement | null | undefined, fond: FondDePapier): void {
  try {
    rangement()?.setItem(CLE_DU_DERNIER_FOND, fond);
  } catch {
    // Rien : le prochain post-it sera jaune.
  }
}

/** Ce qu'on envoie pour poser un post-it : pas de place, le serveur en propose une libre. */
export function postItNeuf(fond: FondDePapier): EnvoiDUnPapier {
  return { sorte: 'post_it', fond };
}

/**
 * Ce qui part au serveur à la sortie d'un champ : seulement ce qui a changé. Le titre perd ses
 * espaces autour, et vidé il devient `null` ; le texte part tel qu'il est écrit. `null` : rien à
 * envoyer.
 */
export function ceQuiAChange(
  papier: Pick<Papier, 'titre' | 'texte'>,
  ecrit: { titre: string; texte: string }
): ChangementDePapier | null {
  const changement: ChangementDePapier = {};
  const titre = ecrit.titre.trim() || null;
  if (titre !== papier.titre) changement.titre = titre;
  if (ecrit.texte !== papier.texte) changement.texte = ecrit.texte;
  return Object.keys(changement).length > 0 ? changement : null;
}

/** Les papiers à dessiner : sans ceux qu'on vient de supprimer, le temps d'« Annuler ». */
export function papiersMontres<P extends { id: string }>(papiers: readonly P[], enSuppression: ReadonlySet<string>): P[] {
  return papiers.filter((p) => !enSuppression.has(p.id));
}
